import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";

import type {
  AdminTicketDetailedListItemDto,
  AdminTicketFiltersDto,
  AdminTicketTabsStatsDto,
  SelfDto,
} from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { ClockIcon } from "@/components/Icons/ClockIcon";
import { DotsVerticalIcon } from "@/components/Icons/DotsVerticalIcon";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { MessageCircle01Icon } from "@/components/Icons/MessageCircle01Icon";
import { Settings01Icon } from "@/components/Icons/Settings01Icon";
import { TicketOperationalAnalyticsSummary } from "@/components/OperationalAnalytics/OperationalAnalyticsSummaries";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { getRemsStatus } from "@/components/Ticket/helpers";
import { RemsStatus } from "@/components/Ticket/RemsStatus";
import { TicketCard } from "@/components/Ticket/TicketCard";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { TicketTableActions } from "@/components/Ticket/TicketTableActions";
import { TicketVisibilityIndicator } from "@/components/Ticket/TicketVisibilityIndicator";
import { User, UserTooltipContent } from "@/components/User/User";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { Button } from "@/design-system/Button";
import { CollectionEmptyState, CollectionLoadingState } from "@/design-system/CollectionState";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { RatingBadge } from "@/design-system/RatingBadge";
import { Stack } from "@/design-system/Stack";
import {
  createDashboardColumnHelper,
  type DashboardTableState,
  TABLE_COLUMN_SIZES,
} from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import type { TabItem } from "@/design-system/Tabs";
import { Tabs } from "@/design-system/Tabs";
import { Text } from "@/design-system/Text";
import { formatAddress } from "@/helpers/address";
import { daysBetween } from "@/helpers/date";
import { canListCategories, canListUsers } from "@/helpers/permissions";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { usePermission } from "@/hooks/usePermission";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { canCreateTicket, canListStatuses } from "@/modules/tickets/permissions";
import { ListPageToolbar } from "@/product-patterns/tickets/collections/ListPageToolbar";
import { ResponsiveCollection } from "@/product-patterns/tickets/collections/ResponsiveCollection";
import { TableCellCount, TableCellDescription } from "@/product-patterns/tickets/collections/TableCellContent";
import type { AdminTicketTabs, TicketSorting } from "@/queries/tickets/constants";
import {
  CLOSED_TICKET_TAB,
  IN_PROGRESS_TICKET_TAB,
  NEW_TICKET_TAB,
  REMINDER_TICKET_TAB,
} from "@/queries/tickets/constants";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { CreateTicketModal } from "./components/CreateTicketModal";
import type { TicketsFilterValues } from "./components/TicketsFilterDrawer";
import { TicketsFilterDrawer } from "./components/TicketsFilterDrawer";
import { getActiveTicketFilterCount, getTicketActions } from "./helpers";

export const AMOUNT_TICKET_LIMIT = 30;

export interface TicketListContentProps {
  tab: AdminTicketTabs;
  onChangeTab: (tab: AdminTicketTabs) => void;
  search: string;
  onChangeSearch: (val: string) => void;
  residentFocusMode: boolean;
  onUpdateResidentFocusMode: (val: boolean) => void;
  sorting: TicketSorting;
  onUpdateSorting: (val: TicketSorting) => void;
  filter: TicketsFilterValues;
  onUpdateFilter: (filter: TicketsFilterValues) => void;
  totalTickets?: number;
  totalTicketsInTab?: number;
  tabsStats?: AdminTicketTabsStatsDto;
  tickets: AdminTicketDetailedListItemDto[];
  isLoadingTickets: boolean;
  hasMoreTickets: boolean | undefined;
  loadMoreTickets: () => void;
  isLoadingMoreTickets: boolean;
  filterOptions: AdminTicketFiltersDto | undefined;
  exportTickets: () => Promise<any>;
  isDownloading: boolean;
  deleteTicket: (payload: { ticketId: string }) => void;
  markAsRead: () => void;
  copyQuickReplyLink: (ticketId: string) => void;
}
export function TicketListContent({
  search,
  onChangeSearch,
  tab,
  onChangeTab,
  filter,
  onUpdateResidentFocusMode,
  totalTickets,
  totalTicketsInTab,
  tabsStats,
  tickets,
  isLoadingTickets,
  hasMoreTickets,
  loadMoreTickets,
  isLoadingMoreTickets,
  filterOptions,
  onUpdateFilter,
  exportTickets,
  isDownloading,
  deleteTicket,
  markAsRead,
  copyQuickReplyLink,
  residentFocusMode,
  sorting,
  onUpdateSorting,
}: TicketListContentProps): React.ReactNode {
  const [isFilterMenuOpened, filterMenuHandler] = useBool(false);
  const [isMarkAsReadModalOpened, markAsReadModalHandler] = useBool(false);
  const [ticketIdToDelete, setTicketIdToDelete] = useState<string | undefined>(undefined);
  const [isCreateTicketModalOpened, createTicketModalHandler] = useBool(false);
  const [isConfirmExportModalOpened, confirmExportModalHandler] = useBool(false);

  const sessionUser = useSessionUser();
  const slug = useSlug();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const hasPermission = usePermission();
  const postHog = usePostHog();

  const tabItems: TabItem<AdminTicketTabs>[] = useMemo(
    () => [
      {
        value: NEW_TICKET_TAB,
        label: tabsStats == null ? t("page.tickets.table.tabs.new.loading") : t("page.tickets.table.tabs-label.new"),
        count: tabsStats?.newTickets,
      },
      {
        value: IN_PROGRESS_TICKET_TAB,
        label:
          tabsStats == null
            ? t("page.tickets.table.tabs.progress.loading")
            : t("page.tickets.table.tabs-label.progress"),
        count: tabsStats?.inProgressTickets,
      },
      {
        value: CLOSED_TICKET_TAB,
        label:
          tabsStats == null ? t("page.tickets.table.tabs.closed.loading") : t("page.tickets.table.tabs-label.closed"),
        count: tabsStats?.closedTickets,
      },
      {
        value: REMINDER_TICKET_TAB,
        label:
          tabsStats == null ? t("page.tickets.table.tabs.later.loading") : t("page.tickets.table.tabs-label.later"),
        icon: <ClockIcon />,
        iconTone: "info",
        isHidden: !tabsStats || tabsStats.reminderTickets === 0,
        count: tabsStats?.reminderTickets,
      },
    ],
    [tabsStats, t],
  );

  const handleClickExport = () => {
    confirmExportModalHandler.setTrue();
  };

  const exportTicketsData = async () => {
    try {
      await exportTickets();
    } finally {
      confirmExportModalHandler.setFalse();
    }
  };

  const handleClickCreateTicket = () => {
    postHog.capture("clicked_create_ticket");

    createTicketModalHandler.setTrue();
  };

  const amountActiveFilters = getActiveTicketFilterCount(filter);
  const isFilterActive = amountActiveFilters > 0 || !!search || residentFocusMode;

  return (
    <>
      <DocumentPage
        tabTitle={t("page.tickets.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.tickets.title"),
              },
            ]}
          />
        }
        actions={
          <>
            {!hasPermission(canListStatuses) && hasPermission(canListCategories) && (
              <Button
                type="link"
                icon={<Settings01Icon />}
                href={routes.ticketCategories.list({
                  slug,
                })}
                variant="soft"
              >
                {t("navigation.tickets.categories")}
              </Button>
            )}
            {!hasPermission(canListCategories) && hasPermission(canListStatuses) && (
              <Button
                type="link"
                icon={<Settings01Icon />}
                href={routes.ticketStatuses.list({
                  slug,
                })}
                variant="soft"
              >
                {t("navigation.tickets.statuses")}
              </Button>
            )}

            {hasPermission(canListStatuses) && hasPermission(canListCategories) && (
              <ContextMenu
                items={[
                  {
                    type: "link",
                    value: "statuses",
                    labels: { default: t("navigation.tickets.statuses") },
                    href: routes.ticketStatuses.list({ slug }),
                  },
                  {
                    type: "link",
                    value: "categories",
                    labels: { default: t("navigation.tickets.categories") },
                    href: routes.ticketCategories.list({ slug }),
                  },
                ]}
                triggerComponent={
                  <IconButton
                    data-testid="share-btn"
                    type="button"
                    variant="soft"
                    icon={<DotsVerticalIcon />}
                    title={t("common.action.see-more")}
                    withTooltip={false}
                  />
                }
              />
            )}
            <AskNobiButton
              defaultFilters={{ projectIds: [projectId] }}
              context={{
                scope: "project",
                entity: "tickets",
                page: "list",
                filters: {
                  tab: tab,
                  search,
                  assigneeIds: filter.assigneeIds,
                  assigneeMode: filter.assigneeMode,
                  statusIds: filter.statusIds,
                  categoryIds: filter.categoryIds,
                  ratings: filter.ratings,
                  ticketPublicity: filter.visibility,
                  addressTypes: filter.addressTypes,
                  closedFrom: filter.closedFrom,
                  closedTo: filter.closedTo,
                  ratedFrom: filter.ratedFrom,
                  ratedTo: filter.ratedTo,
                  unreadOnly: filter.isUnreadOnly,
                  residentFocusMode: residentFocusMode,
                },
              }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.tickets.suggestion.1"),
                t("component.ask-nobi-chat-drawer.tickets.suggestion.2"),
              ]}
            />
            {hasPermission(canCreateTicket) && hasPermission(canListUsers) && (
              <Button data-testid="create-ticket-button" onClick={handleClickCreateTicket}>
                {t("page.tickets.create-btn")}
              </Button>
            )}
          </>
        }
        header={
          <ListPageToolbar
            search={
              <SearchInput
                data-testid="search-input"
                placeholder={t("page.tickets.search.placeholder")}
                defaultValue={search}
                onChange={(e) => onChangeSearch(e.target.value)}
              />
            }
            filters={
              <Button variant="soft" onClick={filterMenuHandler.toggle} icon={<FilterFunnel01Icon />}>
                <Inline gap="sm" align="center" wrap={false}>
                  {t("common.action.filter")}
                  {amountActiveFilters > 0 && <NumericBadge count={amountActiveFilters} variant="filled" />}
                </Inline>
              </Button>
            }
            actions={
              <Button variant="soft" onClick={handleClickExport} icon={<Download01Icon />}>
                {t("common.action.export")}
              </Button>
            }
          />
        }
      >
        <Stack gap="md">
          <TicketOperationalAnalyticsSummary />
          <Tabs.Root<AdminTicketTabs> activeTab={tab} tabs={tabItems} onSelectTab={(tab) => onChangeTab(tab)}>
            <ResponsiveCollection
              controls={
                <TicketTableActions
                  results={totalTickets}
                  total={totalTicketsInTab}
                  onUpdateFocus={(val) => onUpdateResidentFocusMode(val)}
                  sorting={sorting}
                  onSort={onUpdateSorting}
                  isFilterActive={isFilterActive}
                  onMarkAsRead={markAsReadModalHandler.setTrue}
                  tab={tab}
                  isFocusModeOn={residentFocusMode}
                  disableSorting={residentFocusMode && tab === IN_PROGRESS_TICKET_TAB}
                />
              }
              wide={
                <TicketsTable
                  isFilterActive={isFilterActive}
                  tickets={tickets}
                  hasMoreTickets={hasMoreTickets}
                  loadMoreTickets={loadMoreTickets}
                  isLoadingTickets={isLoadingTickets}
                  isLoadingMoreTickets={isLoadingMoreTickets}
                  tab={tab}
                  sessionUser={sessionUser}
                  onDelete={setTicketIdToDelete}
                  onCopyQuickReplyLink={copyQuickReplyLink}
                />
              }
              compact={
                <TicketsMobileView
                  isFilterActive={isFilterActive}
                  tickets={tickets}
                  hasMoreTickets={hasMoreTickets}
                  loadMoreTickets={loadMoreTickets}
                  isLoadingTickets={isLoadingTickets}
                  isLoadingMoreTickets={isLoadingMoreTickets}
                  tab={tab}
                  onDelete={setTicketIdToDelete}
                  onCopyQuickReplyLink={copyQuickReplyLink}
                />
              }
            />
          </Tabs.Root>
        </Stack>

        <ConfirmModal
          title={t("page.tickets.delete.modal.title")}
          description={t("page.tickets.delete.modal.text")}
          isLoading={false}
          tone="error"
          onReject={() => setTicketIdToDelete(undefined)}
          rejectBtnProps={{
            "data-testid": "delete-ticket-modal-cancel",
          }}
          onResolve={() => {
            if (!ticketIdToDelete) {
              return;
            }
            deleteTicket({ ticketId: ticketIdToDelete });
            setTicketIdToDelete(undefined);
          }}
          resolveBtnProps={{
            "data-testid": "delete-ticket-modal-confirm",
            text: t("common.action.delete"),
          }}
          isOpened={ticketIdToDelete !== undefined}
          data-testid="delete-ticket-modal"
          onOpenChange={(state) => {
            if (!state) {
              setTicketIdToDelete(undefined);
            }
          }}
        />
        <ConfirmModal
          title={t("page.tickets.mark-all-as-read.modal.title")}
          description={t("page.tickets.mark-all-as-read.modal.text")}
          isLoading={false}
          tone="primary"
          onReject={markAsReadModalHandler.setFalse}
          rejectBtnProps={{
            "data-testid": "mark-all-as-read-ticket-modal-cancel",
          }}
          onResolve={() => {
            markAsRead();
            markAsReadModalHandler.setFalse();
            postHog.capture("clicked_maar_project_tickets");
          }}
          resolveBtnProps={{
            "data-testid": "mark-all-as-read-ticket-modal-confirm",
            text: t("common.action.confirm"),
          }}
          isOpened={isMarkAsReadModalOpened}
          onOpenChange={markAsReadModalHandler.set}
          data-testid="mark-all-as-read-ticket-modal"
        />
      </DocumentPage>

      <TicketsFilterDrawer
        isOpened={isFilterMenuOpened}
        onOpenChange={filterMenuHandler.set}
        filter={filter}
        filterOptions={
          filterOptions ?? {
            assignees: [],
            statuses: [],
            categories: [],
            addressTypes: [],
          }
        }
        onUpdate={onUpdateFilter}
      />
      <CreateTicketModal
        isOpened={isCreateTicketModalOpened}
        onOpenChange={createTicketModalHandler.set}
        isCreatorResident={false}
      />

      <ConfirmModal
        tone="primary"
        isOpened={isConfirmExportModalOpened}
        onOpenChange={confirmExportModalHandler.set}
        isLoading={isDownloading}
        title={t("page.tickets.export-modal.title")}
        description={t("page.tickets.export-modal.description", {
          maxCount: AMOUNT_TICKET_LIMIT,
        })}
        onReject={() => confirmExportModalHandler.setFalse()}
        onResolve={exportTicketsData}
        resolveBtnProps={{
          text: t("common.action.export"),
        }}
      />
    </>
  );
}
interface TicketsTableProps {
  sessionUser: SelfDto;
  tickets: AdminTicketDetailedListItemDto[];
  hasMoreTickets: boolean | undefined;
  loadMoreTickets: () => void;
  isLoadingTickets: boolean;
  isLoadingMoreTickets: boolean;
  tab: AdminTicketTabs;
  isFilterActive: boolean;
  onDelete: (ticketId: string) => void;
  onCopyQuickReplyLink: (ticketId: string) => void;
}

function TicketsTable({
  tickets,
  hasMoreTickets,
  loadMoreTickets,
  isLoadingTickets,
  isLoadingMoreTickets,
  isFilterActive,
  tab,
  sessionUser,
  onDelete,
  onCopyQuickReplyLink,
}: TicketsTableProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const slug = useSlug();
  const { t, i18n } = useTranslation();

  const isRemsInProject = tickets.some((ticket) => getRemsStatus(ticket));

  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreTickets && hasMoreTickets) {
        loadMoreTickets();
      }
    }, [isLoadingMoreTickets, hasMoreTickets, loadMoreTickets]),
  });
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<AdminTicketDetailedListItemDto>();

    return [
      helper.display({
        id: "visibility",
        cell: (cell) => {
          const value = cell.row.original.visibility;

          if (value === "private") {
            return null;
          }

          return <TicketVisibilityIndicator type={value} />;
        },
        size: TABLE_COLUMN_SIZES.VISUAL_INDICATOR.DEFAULT,
        enableResizing: false,
        meta: {
          isVisualIndicator: true,
        },
      }),
      helper.accessor("status", {
        header: t("page.tickets.table.header.status"),
        cell: (cell) => (
          <TicketStatus labelColor={cell.getValue().color} description={cell.getValue().name} variant="filled" />
        ),
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
      helper.accessor("createdAt", {
        header: t("page.tickets.table.header.created-at"),
        cell: (cell) =>
          t("page.tickets.table.content.created-at", {
            count: daysBetween(cell.getValue(), clockDate),
          }),
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
        meta: {
          renderTruncatedTooltip: (cell) =>
            t("page.tickets.table.content.created-at", {
              count: daysBetween(cell.getValue(), clockDate),
            }),
        },
      }),
      helper.accessor("closedAt", {
        header: t("page.tickets.table.header.closed-at"),
        cell: (cell) =>
          t("page.tickets.table.content.closed-at", {
            when: formatDate(i18n, "dateMonthYearShort", cell.getValue()!),
            who: cell.row.original.closedBy?.fullName || cell.row.original.user.fullName,
          }),
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
        meta: {
          renderTruncatedTooltip: (cell) =>
            t("page.tickets.table.content.closed-at", {
              when: formatDate(i18n, "dateMonthYearShort", cell.getValue()!),
              who: cell.row.original.closedBy?.fullName || cell.row.original.user.fullName,
            }),
        },
      }),
      helper.accessor("title", {
        header: t("page.tickets.table.header.title"),
        cell: (cell) => (
          <Button
            variant="ghostSecondary"
            type="link"
            href={routes.tickets.details({ slug, id: cell.row.original.id })}
          >
            <TableCellDescription
              data-testid="ticket-title-cell"
              primary={cell.getValue()}
              secondary={
                tab === REMINDER_TICKET_TAB
                  ? formatDate(i18n, "datetimeShort", cell.row.original.remindAt!)
                  : t("page.tickets.table.content.title", {
                      when: formatDate(i18n, "datetimeShort", cell.row.original.lastActivityAt),
                      who: cell.row.original.lastActivityBy?.fullName || cell.row.original.user.fullName,
                    })
              }
              secondaryIcon={cell.row.original.remindAt ? <ClockIcon /> : undefined}
              secondaryIconTone="brand"
            />
          </Button>
        ),
        meta: {
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("user", {
        header: t("page.tickets.table.header.reporter"),
        cell: (cell) => (
          <User
            user={cell.getValue()}
            size="sm"
            renderSubtitle={() =>
              cell.row.original.company?.name ||
              (cell.row.original.address ? formatAddress(cell.row.original.address) : undefined)
            }
            shouldNavigate={false}
          />
        ),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
        meta: {
          renderTruncatedTooltip: (cell) => (
            <UserTooltipContent
              fullName={cell.row.original.user.fullName}
              address={
                cell.row.original.company?.name ||
                (cell.row.original.address ? formatAddress(cell.row.original.address) : undefined)
              }
            />
          ),
        },
      }),
      helper.accessor("rating", {
        header: t("page.tickets.table.header.rating"),
        cell: (cell) => {
          const data = cell.getValue();

          if (data) {
            return <RatingBadge rating={data} />;
          }
        },
        minSize: TABLE_COLUMN_SIZES.STATUS.MIN,
        size: TABLE_COLUMN_SIZES.STATUS.DEFAULT,
      }),
      helper.accessor("activityCount", {
        header: t("page.tickets.table.header.activity"),
        cell: (cell) => {
          return (
            <TableCellCount icon={<MessageCircle01Icon />} emphasized={cell.row.original.hasUnreadActivity}>
              {cell.getValue()}
            </TableCellCount>
          );
        },
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("assignee", {
        header: t("page.tickets.table.header.assignee"),
        cell: (cell) => {
          const data = cell.getValue();

          if (!data) {
            return (
              <Inline gap="sm" align="center" wrap={false}>
                <UserAvatar size="sm" image={undefined} />
                <Text variant="caption-emphasis">{t("page.tickets.table.content.unassigned")}</Text>
              </Inline>
            );
          }

          return <User user={data} size="sm" shouldNavigate={false} />;
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
        meta: {
          renderTruncatedTooltip: (cell) => {
            const data = cell.getValue();

            return <UserTooltipContent fullName={data ? data.fullName : t("page.tickets.table.content.unassigned")} />;
          },
        },
      }),
      ...(isRemsInProject
        ? [
            helper.accessor("integrations", {
              id: "rems",
              header: "",
              cell: ({ cell }) => {
                const data = getRemsStatus(cell.row.original);

                if (!data) {
                  return null;
                }

                return <RemsStatus rems={data} />;
              },
              minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
              size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
            }),
          ]
        : []),
      helper.display({
        id: "actions",
        cell: ({ cell }) => {
          const value = cell.row.original.id;

          const menuActions = getTicketActions(
            t,
            sessionUser,
            () => onDelete(value),
            () => onCopyQuickReplyLink(value),
          );

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [clockDate, t, isRemsInProject, i18n, slug, tab, sessionUser, onDelete, onCopyQuickReplyLink]);
  const tableState = useMemo(
    () =>
      ({
        columnVisibility: {
          status: tab !== NEW_TICKET_TAB || (tab === NEW_TICKET_TAB && sessionUser.isAdmin),
          closedAt: tab === CLOSED_TICKET_TAB,
          createdAt: tab !== CLOSED_TICKET_TAB,
          rating: tab === CLOSED_TICKET_TAB,
          assignee: tab !== CLOSED_TICKET_TAB,
        },
      }) satisfies Partial<DashboardTableState>,
    [sessionUser.isAdmin, tab],
  );
  const table = useResizableTable(TABLE_KEYS.TICKETS_ADMIN, {
    columns: columns,
    data: tickets,
    state: tableState,
    enableSorting: false,
  });

  return (
    <>
      {(tickets.length > 0 || isLoadingTickets) && (
        <Table
          table={table}
          getRowHref={(row) =>
            routes.tickets.details({
              slug,
              id: row.original.id,
            })
          }
          isLoading={isLoadingTickets && !isLoadingMoreTickets}
        />
      )}
      {tickets.length === 0 && !isLoadingTickets && (
        <CollectionEmptyState>
          {isFilterActive ? t("page.tickets.table.no-results") : t("page.tickets.table.empty")}
        </CollectionEmptyState>
      )}
      {hasMoreTickets && <CollectionLoadingState ref={ref} />}
    </>
  );
}

type TicketsMobileViewProps = Omit<TicketsTableProps, "sessionUser">;

function TicketsMobileView({
  isFilterActive,
  hasMoreTickets,
  isLoadingMoreTickets,
  isLoadingTickets,
  loadMoreTickets,
  tab,
  tickets,
  onDelete,
  onCopyQuickReplyLink,
}: TicketsMobileViewProps) {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreTickets && hasMoreTickets) {
        loadMoreTickets();
      }
    }, [isLoadingMoreTickets, hasMoreTickets, loadMoreTickets]),
  });

  return isLoadingTickets ? (
    <CollectionLoadingState />
  ) : (
    <>
      {tickets.length > 0 ? (
        <Stack gap="lg">
          {tickets.map((ticket) => {
            const currMenuActions = getTicketActions(
              t,
              sessionUser,
              () => onDelete(ticket.id),
              () => onCopyQuickReplyLink(ticket.id),
            );

            return (
              <TicketCard
                ticket={ticket}
                showOpenedSince={tab === NEW_TICKET_TAB}
                showRating={tab === CLOSED_TICKET_TAB}
                key={ticket.id}
                menuActions={currMenuActions}
              />
            );
          })}
        </Stack>
      ) : (
        <CollectionEmptyState presentation="compact">
          {isFilterActive ? t("page.tickets.table.no-results") : t("page.tickets.table.empty")}
        </CollectionEmptyState>
      )}
      {hasMoreTickets && <CollectionLoadingState ref={ref} show={isLoadingTickets || isLoadingMoreTickets} />}
    </>
  );
}
